import { CommonModule } from '@angular/common';
import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { RouteId } from '../app.routes';
import { BackendService } from '../core/backend.service';
import { DialogService, panelClassMobileFullscreen } from '../core/dialog.service';
import { LocaleService } from '../core/locale/locale.service';
import { ResponsiveDesignService } from '../core/responsive-design.service';
import { ResetObjectSelection } from '../root-store/actions';
import { LoadCart } from '../root-store/cart-store/actions';
import { selectCartItemCount, selectCartModel, selectOrder } from '../root-store/cart-store/selectors';
import { selectCatalog } from '../root-store/catalog-store/selectors';
import { CatalogSettings, CatalogState } from '../root-store/catalog-store/state';
import { RootState } from '../root-store/root-state.model';
import { selectAdminCartMode, selectOrderId, selectOrderIdStatusPage, selectRouteId } from '../root-store/router/selectors';
import { LoadUser } from '../root-store/user-store/actions';
import { selectIsAuthenticated, selectUser } from '../root-store/user-store/selectors';
import { LanguageSwitchComponent } from '../shared/language-switch/language-switch.component';
import { TippyDirective } from '../shared/tippy.directive';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [
    CommonModule, FormsModule, RouterLink, RouterLinkActive, TranslateModule, TippyDirective,
    MatIconModule, MatToolbarModule, MatBadgeModule, MatDividerModule,
    MatButtonModule, MatMenuModule, LanguageSwitchComponent,
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  catalog!: CatalogState
  settings!: CatalogSettings
  activeRouteId!: string
  RouteId = RouteId

  user$ = this.store.select(selectUser)
  cartCount$ = this.store.select(selectCartItemCount).pipe(map(count => !count || count < 99 ? count : 99))
  isAuthenticated$ = this.store.select(selectIsAuthenticated)
  order$ = this.store.select(selectOrder)
  orderId$ = this.store.select(selectOrderId)
  isSmallScreen$ = this.responsive.isSmallScreen$
  cart$ = this.store.select(selectCartModel)
  currentLang = this.localeService.currentLang
  adminCartMode = false
  @HostBinding('class.fixed-header') fixedHeader = false

  private subs = new SubSink()
  showLogoOnly = false

  constructor(
    private store: Store<RootState>,
    public localeService: LocaleService,
    private backend: BackendService,
    private responsive: ResponsiveDesignService,
    private dialogService: DialogService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.catalog = this.store.selectSignal(selectCatalog)()
    this.settings = this.catalog.settings

    this.store.select(selectRouteId).subscribe(id => this.activeRouteId = id)

    // if cart is not yet loaded, load cart once at Startup (except if cart-component is currently shown)
    const cart = this.store.selectSignal(selectCartModel)()
    const currentRouteId = this.store.selectSignal(selectRouteId)()

    if (!cart && currentRouteId !== RouteId.Cart && currentRouteId !== RouteId.OrderStatus && currentRouteId !== RouteId.OrderSuccess)
      this.store.dispatch(LoadCart())


    // handle display in iFrame: when order-status is shown in iframe, hide and disable all links
    // this is when the order-status is shown via my-orders
    if (window.self !== window.top)
      this.store.select(selectOrderIdStatusPage).subscribe(orderId => this.showLogoOnly = !!orderId)

    this.subs.sink = combineLatest([
      this.store.select(selectAdminCartMode),
      this.store.select(selectRouteId)
    ]).subscribe(([adminMode, currentRouteId]) => {
      this.adminCartMode = adminMode
      this.fixedHeader = adminMode && currentRouteId === RouteId.Cart
    })
  }

  onHomeClick(): void {
    this.store.dispatch(ResetObjectSelection())
  }

  async openMyOrders() {
    this.dialog.open(
      (await import('../user/my-orders/my-orders.component')).MyOrdersComponent,
      {
        autoFocus: false,
        panelClass: panelClassMobileFullscreen
      })
  }

  onEditProfileClick(): void {
    this.dialogService.openEditProfile()
  }

  onPasswordChangeClick(): void {
    this.dialogService.openChangePassword()
  }

  async onLoginClick() {
    this.dialog.open((await import('../user/login/login.component')).LoginComponent)
  }

  onLogoutClick(): void {
    this.backend.logoutUser().subscribe(() => this.store.dispatch(LoadUser()))
  }

  ngOnDestroy() {
    this.subs.unsubscribe()
  }
}
